import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import {
  BehaviorSubject,
  filter,
  finalize,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocationProfilePopupComponent } from 'src/app/components/admin/location-admin/location-profile-popup/location-profile-popup.component';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { AdminSearchCriteria, SpecialCharacters } from 'src/app/core/enums';
import {
  Location,
  LocationProfile,
  PostLocationProfileResponse,
} from 'src/app/core/models/admin/location-admin.model';
import { UserAdmin } from 'src/app/core/models/admin/user-admin.model';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { LocationPermissionUtils } from 'src/app/core/utils/permissions/location-permissions/location-permissions.utils';
import {
  ERROR_TEXT,
  IMAGE_SRC,
  NO_RESULTS,
  SELECT_ALL_TEXT,
  STATUS_OPTIONS,
} from '../../shared-constants';
import {
  ColumnIds,
  ColumnText,
  CONSTS,
  EXCEL_FILE_NAME,
  FilteredLocation,
  SelectedLocationType,
} from './location-admin-table.constants';

@Component({
  selector: 'app-location-admin-table',
  templateUrl: './location-admin-table.component.html',
  styleUrls: ['./location-admin-table.component.scss'],
  standalone: false,
})
export class LocationAdminTableComponent implements OnInit, AfterViewInit {
  @Input() radioHeader!: string;
  @Input() firstOptionValue!: string;
  @Input() firstOptionName!: string;
  @Input() secondOptionValue!: string;
  @Input() secondOptionName!: string;
  @Input() headerText = CONSTS.WELCOME_TEXT;
  @Input() hasRadioButton = false;
  @Input() radioModel!: AdminSearchCriteria;
  @Input() isAdminSearch = false;
  radioButtonChange = output<Event>();

  private userService = inject(UserService);

  readonly displayedColumns = Object.values(ColumnIds);
  readonly COLUMNTEXT = ColumnText;
  readonly STATUS_OPTIONS = STATUS_OPTIONS;
  readonly IMAGES = IMAGE_SRC;
  readonly NO_RESULTS = NO_RESULTS;
  readonly ERROR_TEXT = ERROR_TEXT;
  readonly SELECT_ALL = SELECT_ALL_TEXT;
  readonly CONSTS = CONSTS;
  readonly SPECIAL_CHARS = SpecialCharacters;

  // Create admin table columns
  locationCodeData = this.commonUtils.createTableColumn(
    ColumnIds.LOCATION_CODE,
  );
  locationNameData = this.commonUtils.createTableColumn(
    ColumnIds.LOCATION_NAME,
  );
  portCodeData = this.commonUtils.createTableColumn(ColumnIds.PORT_CODE);
  packageSlicData = this.commonUtils.createTableColumn(ColumnIds.PACKAGE_SLIC);
  countryData = this.commonUtils.createTableColumn(ColumnIds.COUNTRY);
  regionData = this.commonUtils.createTableColumn(ColumnIds.REGION);
  districtData = this.commonUtils.createTableColumn(ColumnIds.DISTRICT);
  timeZoneData = this.commonUtils.createTableColumn(ColumnIds.TIME_ZONE);
  locationGroupsData = this.commonUtils.createTableColumn(
    ColumnIds.LOCATION_GROUPS,
  );
  messagingData = this.commonUtils.createTableColumn(ColumnIds.MESSAGING);
  apiKeyData = this.commonUtils.createTableColumn(ColumnIds.API_KEY);

  // image source variables
  checkboxSrc = IMAGE_SRC.CHECKBOX;
  chevronSrc = IMAGE_SRC.CHEVRON;
  activeSrc = IMAGE_SRC.ACTIVE;
  cancelSrc = IMAGE_SRC.CANCEL;

  removeLocation = false;
  removeLocationConfirm = false;
  selectedLocation!: SelectedLocationType;

  dataSource = new MatTableDataSource<Location>();
  removeUser = false;
  edit = false;
  currentUser!: UserAdmin | undefined;
  locationData: Location[] = [];
  resetSelectDropdowns = new BehaviorSubject<boolean>(false);
  dropdownOpen = [false, false, false];
  timeZoneDropDown: Items[] = [];
  editedRecordIndex!: number;
  readonly destroy = inject(DestroyRef);

  constructor(
    public commonUtils: CommonUtils,
    private readonly dialog: MatDialog,

    private readonly locationService: LocationService,
    private readonly roleUtils: LocationPermissionUtils,
  ) {}

  ngOnInit(): void {
    this.currentUser = {} as UserAdmin;
    this.commonUtils.updateLoadingModal(true);

    this.userService
      .getCurrentUser$()
      .pipe(
        filter((user) => !!user),
        switchMap((user) => {
          this.currentUser = user;

          return this.getLocationData(user?.locationGroupId).pipe(
            tap(() => {
              const data = [
                ...new Set(this.dataSource.data.map((val) => val.timeZone)),
              ];
              this.timeZoneDropDown = data
                .map((val) => {
                  return { id: val, selected: true };
                })
                .sort((a, b) => a.id.localeCompare(b.id));
            }),
          );
        }),
        finalize(() => this.commonUtils.updateLoadingModal(false)),
      )
      .subscribe();

    this.locationService
      .getEditedLocation$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((locationProfile) => !!locationProfile),
      )
      .subscribe((resp) => {
        if (this.editedRecordIndex !== undefined) {
          const tableData = this.dataSource.data;
          tableData[this.editedRecordIndex] = {
            ...(resp as unknown as Location),
            locationCode: resp.locationDetails.code,
            locationName: resp.locationDetails.name,
            messaging: resp.configurations.externalSystems.messagingActive,
          };
          this.dataSource.data = [...tableData];
        }
      });
  }

  ngAfterViewInit() {
    this.dataSource.filterPredicate = this.commonUtils.createFilter();
  }

  getLocationData(groupId: number | undefined): Observable<void> {
    if (groupId !== undefined) {
      return this.locationService
        .getLocationsByGroup(
          this.isAdminSearch ? null : groupId,
          this.isAdminSearch,
        )
        .pipe(
          take(1),
          map((locations) => {
            const locationData = locations?.response?.locations;
            locationData?.sort((a, b) =>
              a.locationCode.localeCompare(b.locationCode),
            );
            this.locationData = locationData || [];
            this.dataSource.data = this.locationData;
            this.clearAllFilters();
          }),
        );
    } else {
      return of();
    }
  }

  setTimeZoneOption() {
    const seen = new Set();
    this.timeZoneDropDown = this.dataSource.data
      .filter((location) => {
        if (seen.has(location.timeZone)) return false;
        seen.add(location.timeZone);
        return true;
      })
      .map((location) => ({
        id: location.timeZone,
        selected: true,
      }));
  }

  onRemoveLocation() {
    const tableData = this.dataSource.data;
    tableData.splice(this.selectedLocation.index, 1);
    this.dataSource.data = [...tableData];
    this.removeLocationConfirm = false;
  }

  exportExcel(): void {
    const mappedTableData = this.simplifyDataSource();
    this.commonUtils.exportExcel(
      Object.values(ColumnText),
      EXCEL_FILE_NAME,
      mappedTableData,
    );
  }

  clearAllFilters() {
    this.commonUtils.clearAllFilters(
      this.dataSource,
      this.locationData,
      [
        this.locationCodeData.formControl,
        this.locationNameData.formControl,
        this.portCodeData.formControl,
        this.packageSlicData.formControl,
        this.locationGroupsData.formControl,
        this.countryData.formControl,
        this.regionData.formControl,
        this.districtData.formControl,
      ],
      [this.timeZoneData, this.messagingData, this.apiKeyData],
    );
    this.resetSelectDropdowns.next(true);
  }

  search() {
    this.commonUtils.searchFilter(
      [
        this.locationCodeData,
        this.locationNameData,
        this.portCodeData,
        this.packageSlicData,
        this.locationGroupsData,
        this.countryData,
        this.regionData,
        this.districtData,
      ],
      this.dataSource,
      [this.timeZoneData, this.messagingData, this.apiKeyData],
    );
  }

  onStatusChangeForDropdowns(
    $event: string | boolean | unknown[],
    field: TableColumn,
  ) {
    field.ids[0] = $event as unknown as Items;
    this.search();
  }

  addLocation() {
    this.edit = false;
    this.commonUtils.updateBanner(false);
    this.openProfile$({} as LocationProfile, false).subscribe();
  }

  editLocation(data: Location, index: number) {
    this.editedRecordIndex = index;
    this.commonUtils.updateBanner(false);
    this.locationService
      .getLocationProfile(data.locationCode)
      .pipe(
        take(1),
        mergeMap((profile) => {
          return this.openProfile$(profile?.response, true);
        }),
      )
      .subscribe();
  }

  openProfile$(profile: LocationProfile, edit?: boolean) {
    return this.dialog
      .open(LocationProfilePopupComponent, {
        disableClose: true,
        data: {
          profile: profile,
          edit: edit,
        },
      })
      .afterClosed()
      .pipe(
        mergeMap((response: PostLocationProfileResponse) => {
          if (response?.code === 200) {
            this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
            return this.getLocationData(this.currentUser?.locationGroupId);
          } else {
            return of(false);
          }
        }),
      );
  }

  emitRadioButtonChange($event: Event) {
    this.radioButtonChange.emit($event);
  }

  private simplifyDataSource(): FilteredLocation[] {
    const temp: FilteredLocation[] = [];

    this.dataSource.filteredData.forEach((item) => {
      temp.push({
        locationCode: item.locationCode.toString(),
        locationName: item.locationName,
        portCode: item.portCode,
        pkgSlic: item.pkgSlic,
        country: item.country,
        region: item.region,
        district: item.district,
        timeZone: item.timeZone,
        locationGroups: item.locationGroups,
        messaging: item.messaging,
        hasApiKey: item.hasApiKey,
      });
    });
    return temp;
  }

  get canEditDeleteLocation() {
    const role = this.currentUser?.roleName;
    return this.roleUtils.canEditLocation(role);
  }

  get canAddLocations() {
    const role = this.currentUser?.roleName;
    return this.roleUtils.canAddLocation(role);
  }

  get isFilterEnabled() {
    return (
      this.locationCodeData.formControl.value !== '' ||
      this.locationNameData.formControl.value !== '' ||
      this.portCodeData.formControl.value !== '' ||
      this.packageSlicData.formControl.value !== '' ||
      this.locationGroupsData.formControl.value !== '' ||
      this.countryData.formControl.value !== '' ||
      this.regionData.formControl.value !== '' ||
      this.districtData.formControl.value !== '' ||
      this.timeZoneData.ids.length > 0 ||
      this.messagingData.ids.length > 0 ||
      this.apiKeyData.ids.length > 0
    );
  }
}
